import { useCallback } from 'react';
import { useFormContext } from '@common-fe/common-fe';

import {
  PayoutDefinitionField,
  PayoutDefinitionFormValues,
  PayoutOrderCategory,
} from '../../../PayoutDefinitionSetup.types';

import { collectValidationErrors, getServiceColumnErrorKey } from './usePayoutOrderValidation.utils';

export { getServiceColumnErrorKey, GLOBAL_VALIDATION_MESSAGE } from './usePayoutOrderValidation.utils';
export type { ServiceValidationErrorType } from './usePayoutOrderValidation.utils';

const useDynamicFormErrors = () => {
  const { setError, clearErrors } = useFormContext<PayoutDefinitionFormValues>();

  const setDynamicError = useCallback(
    (key: string, error: { type: string; message: string }) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      setError(key as any, error);
    },
    [setError]
  );

  const clearDynamicErrors = useCallback(
    (keys: string[]) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      clearErrors(keys as any);
    },
    [clearErrors]
  );

  return { setDynamicError, clearDynamicErrors };
};

export const usePayoutOrderValidation = () => {
  const { getValues, formState } = useFormContext<PayoutDefinitionFormValues>();
  const { setDynamicError, clearDynamicErrors } = useDynamicFormErrors();

  /** Read a dynamic error message from formState.errors by key */
  const getDynamicError = useCallback(
    (key: string): string | null => {
      return (formState.errors as Record<string, { message?: string }>)?.[key]?.message ?? null;
    },
    [formState.errors]
  );

  const validate = useCallback(
    (categories: PayoutOrderCategory[]): { isValid: boolean; errorCategoryTypes: Set<string> } => {
      const columnsCount = getValues(PayoutDefinitionField.columnsCount);
      const allServices = categories.flatMap((c) => c.services);

      // Clear all previous per-field errors before re-validating
      const allFieldKeys = allServices.flatMap((service) =>
        Array.from({ length: columnsCount }, (_, col) => getServiceColumnErrorKey(service.rowId, col + 1))
      );
      clearDynamicErrors(allFieldKeys);

      // Collect errors using pure function
      const errors = collectValidationErrors(allServices, columnsCount);

      // Determine which categories have errors
      const errorCategoryTypes = new Set<string>();
      for (const category of categories) {
        const rowIds = new Set(category.services.map((service) => service.rowId));
        if (
          errors.some((e) => {
            const match = e.key.match(/^service_(.+)_col_\d+$/);
            return match && rowIds.has(match[1]);
          })
        ) {
          errorCategoryTypes.add(category.categoryType);
        }
      }

      // Apply errors to form state (only per-service errors, no global key)
      for (const error of errors) {
        setDynamicError(error.key, { type: error.type, message: error.message });
      }

      if (errors.length > 0) {
        return { isValid: false, errorCategoryTypes };
      }

      return { isValid: true, errorCategoryTypes: new Set<string>() };
    },
    [setDynamicError, clearDynamicErrors, getValues]
  );

  return { validate, getDynamicError };
};
